import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@material-ui/core';

import { useAdminGetDashboardReservationsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardReservationsDataQuery';
import { icons } from '../../themes/icons';
import { calculatePercentageChange } from '../../utils/dashboard';
import { toLocaleString } from '../../utils/format-number';
import { CardError } from './CardError';
import StatCard from './StatCard';

type Props = {
  fromDate: string | null;
  priorFrom: string | null;
  toDate: string | null;
  priorTo: string | null;
  selectedLabel: string;
  marketID: string | null;
};

export const ReservationsV2: React.FC<Props> = ({
  fromDate,
  toDate,
  priorFrom,
  priorTo,
  selectedLabel,
  marketID,
}) => {
  const { data, isFetching, isError, refetch } = useAdminGetDashboardReservationsDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
    marketID,
  });

  const totalReservations =
    (data?.total1StoryReservationsCurrent || 0) +
    (data?.total2StoriesReservationsCurrent || 0) +
    (data?.total3StoriesReservationsCurrent || 0) +
    (data?.totalFeedReservationsCurrent || 0) +
    (data?.totalReelsReservationsCurrent || 0) +
    (data?.totalTikTokReservationsCurrent || 0) +
    (data?.totalNonSocialReservationsCurrent || 0);

  const totalPriorReservations =
    (data?.total1StoryReservationsPrior || 0) +
    (data?.total2StoriesReservationsPrior || 0) +
    (data?.total3StoriesReservationsPrior || 0) +
    (data?.totalFeedReservationsPrior || 0) +
    (data?.totalReelsReservationsPrior || 0) +
    (data?.totalTikTokReservationsPrior || 0) +
    (data?.totalNonSocialReservationsPrior || 0);

  const {
    totalPercentageReservations,
    totalPercentageStory,
    totalPercentage2Stories,
    totalPercentage3Stories,
    totalPercentageFeed,
    totalPercentageReel,
    totalPercentageTiktok,
    totalPercentageGift,
  } = React.useMemo(() => {
    const totalPercentageReservations = calculatePercentageChange(
      totalReservations,
      totalPriorReservations,
    );
    const totalPercentageStory = calculatePercentageChange(
      data?.total1StoryReservationsCurrent || 0,
      data?.total1StoryReservationsPrior || 0,
    );
    const totalPercentage2Stories = calculatePercentageChange(
      data?.total2StoriesReservationsCurrent || 0,
      data?.total2StoriesReservationsPrior || 0,
    );
    const totalPercentage3Stories = calculatePercentageChange(
      data?.total3StoriesReservationsCurrent || 0,
      data?.total3StoriesReservationsPrior || 0,
    );
    const totalPercentageFeed = calculatePercentageChange(
      data?.totalFeedReservationsCurrent || 0,
      data?.totalFeedReservationsPrior || 0,
    );
    const totalPercentageReel = calculatePercentageChange(
      data?.totalReelsReservationsCurrent || 0,
      data?.totalReelsReservationsPrior || 0,
    );
    const totalPercentageTiktok = calculatePercentageChange(
      data?.totalTikTokReservationsCurrent || 0,
      data?.totalTikTokReservationsPrior || 0,
    );
    const totalPercentageGift = calculatePercentageChange(
      data?.totalNonSocialReservationsCurrent || 0,
      data?.totalNonSocialReservationsPrior || 0,
    );
    return {
      totalPercentageReservations,
      totalPercentageStory,
      totalPercentage2Stories,
      totalPercentage3Stories,
      totalPercentageFeed,
      totalPercentageReel,
      totalPercentageTiktok,
      totalPercentageGift,
    };
  }, [data]);

  return (
    <BoxContainer>
      <AllStats>
        <Heading>
          <Link to={'/reservations'} style={{ color: 'inherit', textDecoration: 'none' }}>
            Reservations
          </Link>
        </Heading>
        <Time>{selectedLabel}</Time>
      </AllStats>
      {isError && !isFetching && <CardError onRetry={() => refetch()} />}
      {!isError && (
        <>
          <StatCard
            isFetching={isFetching}
            isDesktop
            name="Reservations"
            count={toLocaleString(totalReservations)}
            compare={`${totalPercentageReservations}%`}
            icon={<icons.ReservationBlueIcon />}
            percentageNumber={totalPercentageReservations}
          />
          <NestedBlock>
            <StatCard
              isFetching={isFetching}
              isDesktop
              name="Story"
              count={toLocaleString(data?.total1StoryReservationsCurrent || 0)}
              compare={`${totalPercentageStory}%`}
              icon={<icons.Story />}
              percentageNumber={totalPercentageStory}
            />
            <StatCard
              isFetching={isFetching}
              isDesktop
              name="2 Stories"
              count={toLocaleString(data?.total2StoriesReservationsCurrent || 0)}
              compare={`${totalPercentage2Stories}%`}
              icon={<icons.Story />}
              percentageNumber={totalPercentage2Stories}
            />
            <StatCard
              isFetching={isFetching}
              isDesktop
              name="3 Stories"
              count={toLocaleString(data?.total3StoriesReservationsCurrent || 0)}
              compare={`${totalPercentage3Stories}%`}
              icon={<icons.Story />}
              percentageNumber={totalPercentage3Stories}
            />
            <StatCard
              isFetching={isFetching}
              isDesktop
              name="Feed"
              count={toLocaleString(data?.totalFeedReservationsCurrent || 0)}
              compare={`${totalPercentageFeed}%`}
              icon={<icons.Feed />}
              percentageNumber={totalPercentageFeed}
            />
            <StatCard
              isFetching={isFetching}
              isDesktop
              name="Reel"
              count={toLocaleString(data?.totalReelsReservationsCurrent || 0)}
              compare={`${totalPercentageReel}%`}
              icon={<icons.Reel />}
              percentageNumber={totalPercentageReel}
            />
            <StatCard
              isFetching={isFetching}
              isDesktop
              name="TikTok"
              count={toLocaleString(data?.totalTikTokReservationsCurrent || 0)}
              compare={`${totalPercentageTiktok}%`}
              icon={<icons.TiktokIcon />}
              percentageNumber={totalPercentageTiktok}
            />
            <StatCard
              isFetching={isFetching}
              isDesktop
              name="Gift"
              count={toLocaleString(data?.totalNonSocialReservationsCurrent || 0)}
              compare={`${totalPercentageGift}%`}
              icon={<icons.GiftingIcon />}
              percentageNumber={totalPercentageGift}
            />
          </NestedBlock>
        </>
      )}
    </BoxContainer>
  );
};

const BoxContainer = styled.div`
  border: 1px solid #00000014;
  border-radius: 8px;
  padding: 30px;
`;
const AllStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #0000000d;
  padding-bottom: 15px;
`;

const Heading = styled.div`
  color: #111111;
  font-size: 18px;
  font-family: Poppins;
  font-weight: 500;
`;

const Time = styled.div`
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
  font-family: Poppins;
  font-weight: 500;
`;

const NestedBlock = styled.div`
  margin-left: 40px;
`;
